import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { makeTestId } from '../../utils/Declarative';
import { addClientRectLeft, addClientRectRight } from '../../utils/Geometry';
import { getSliderHandleStyles, SliderHandleIconSize, SliderHandleStyleName, SliderHandleSvgStyleName } from './SliderStyles';

const FocusIndicatorStyleName = TypeStyle.style({ // HACK THE PLANET
  $nest: {
    '&.focus-visible:focus-within::after': { // c.f. https://github.com/w3c/csswg-drafts/issues/3080
      content: `""`,
      pointerEvents: 'none',
      width: 16,
      height: 16,
      display: 'inline-block',
      borderRadius: '50%',
      top: 12,
      left: -8,
      position: 'absolute',
      ...DefaultFocusIndicator(),
    },
    '&:not(:only-child):focus-within::after': {
      borderTopLeftRadius: '66.66% 50%',
      borderBottomLeftRadius: '66.66% 50%',
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
      left: -12,
      width: 13,
    },
    '&:not(:only-child):last-child:focus-within::after': {
      transform: 'rotate(180deg)',
      transformOrigin: '12px 8px',
    }
  }
});

export enum SliderHandleIconType {
  Normal = 'normal',
  Left = 'left',
  Right = 'right'
}

export interface SliderHandleProps {
  ariaAttributes: Pick<React.AriaAttributes,
    'aria-label' | 'aria-labelledby' | 'aria-valuemax' | 'aria-valuemin' | 'aria-valuenow' | 'aria-valuetext'
  >;

  disabled?: boolean;

  /** @default SliderHandleIconType.normal */
  iconType?: SliderHandleIconType;

  isActive: boolean;
  isBeingTouched: boolean;
  onKeyDown?: React.KeyboardEventHandler;
  onKeyUp?: React.KeyboardEventHandler;

  /* A proportion (0.0 to 1.0 inclusive) of where the center of the handle is horizontally inside its containing parent. */
  position: number;

  /** Test id to be attached to the slider and its contents. The suffix `'-SliderHandle'` will be appended.   */
  testId: string;
}

export interface SliderHandleState {
  focusVisible: boolean;
}

/**
 * This is just the handle part of the slider.
 *
 * It is interactive, BUT that logic (e.g. dragging) is handled by the container.  This allows some stuff like clicking
 * anywhere on the track to grab and drag the handle, rather than only being able to click on the handle.
 */
export class SliderHandleWidget extends React.Component<SliderHandleProps, SliderHandleState> {
  // We capture this ref so that we can 'focus()' it, and it is used indirectly for some hit testing logic in the
  // SliderHandleContainer.
  private imgRef: SVGSVGElement | null = null;
  // We capture this ref so that can give its client bounding box out to the SliderHandleContainer.  This area is
  // effectively the handle's touch target (or mouse target) area.
  private interactiveAreaRef: HTMLElement | null = null;

  public state: SliderHandleState = { focusVisible: true };

  private chooseIconAndStylesForRender = (): {
    positioningContainerStyle: React.CSSProperties,
    pressIndicationBarStyle: React.CSSProperties,
    interactiveAreaStyle: React.CSSProperties,
    svgPaths: React.ReactElement,
  } => {
    const { disabled, iconType } = this.props;
    const styles = getSliderHandleStyles();

    let interactiveAreaStyle = undefined;
    let svgPaths = undefined;

    switch (iconType) {
      case SliderHandleIconType.Left:
        interactiveAreaStyle = styles.HandleInteractiveAreaLeft;
        svgPaths = <path d='M11.2,2.5c-4.3,0-7.7,3.3-7.7,7.5s3.5,7.5,7.8,7.5h4.3v-15H11.2z' />;
        break;
      case SliderHandleIconType.Right:
        interactiveAreaStyle = styles.HandleInteractiveAreaRight;
        svgPaths = <path d='M7.8,2.6c4.3,0,7.8,3.3,7.8,7.4s-3.5,7.4-7.8,7.4H3.5V2.6H7.8z' />;
        break;
      default:
        interactiveAreaStyle = styles.HandleInteractiveArea;
        svgPaths = <circle cx='10' cy='10' r='7.5' />;
        break;
    }

    if (disabled) {
      interactiveAreaStyle = { ...interactiveAreaStyle, ...styles.DisabledHandleMixin };
    }

    return {
      positioningContainerStyle: styles.PositioningContainer,
      pressIndicationBarStyle: styles.PressIndicationBar,
      interactiveAreaStyle,
      svgPaths,
    };
  }

  private handleImgRef = (element: SVGSVGElement | null): void => {
    this.imgRef = element;
  }

  private handleInteractiveAreaRef = (element: HTMLElement | null): void => {
    this.interactiveAreaRef = element;
  }

  public focus(): void {
    this.imgRef?.focus();
  }

  public getBoundingClientRectForIcon(): ClientRect {
    if (this.imgRef) {
      const rect = this.imgRef.getBoundingClientRect();
      // The left and right icons have several pixels of transparency between what appears like the edge of the artwork
      // and the actual edge of the image.  Unfortunately we are using the client bounding rect of the icon for some
      // behavior (e.g. see the dragStart method in SliderHandleContainer, which has logic that has to know whether
      // the user (with a mouse) clicked specifically on the icon or not).
      // To make the actual client bounding rect matchup visually with the edge of the artwork, we have to shrink
      // the bounding rect slightly.
      if (this.props.iconType === SliderHandleIconType.Left) {
        return addClientRectRight(rect, -4);
      } else if (this.props.iconType === SliderHandleIconType.Right) {
        return addClientRectLeft(rect, 2);
      }
      return rect;
    } else {
      throw new Error(`Can't determinte bounding rectangle of unmounted handle`);
    }
  }

  public getBoundingClientRectForInteractiveArea(): ClientRect {
    if (this.interactiveAreaRef) {
      return this.interactiveAreaRef.getBoundingClientRect();
    } else {
      throw new Error(`Can't determinte bounding rectangle of unmounted element`);
    }
  }

  public render(): JSX.Element {
    const { disabled, isActive, iconType } = this.props;
    const dataTestId = makeTestId(this.props.testId, 'SliderHandle');
    const positionAsPercentage = this.props.position * 100;

    const { positioningContainerStyle, pressIndicationBarStyle, interactiveAreaStyle, svgPaths } = this.chooseIconAndStylesForRender();

    const renderTouchDragIndicator: boolean = isActive && this.props.isBeingTouched;

    return (
      <span
        className={TypeStyle.classes(FocusIndicatorStyleName, { 'focus-visible': this.state.focusVisible })}
        data-tb-test-id={dataTestId}
        onBlurCapture={() => this.setState({ focusVisible: true })}
        onKeyDownCapture={() => this.setState({ focusVisible: true })}
        onPointerDownCapture={() => this.setState({ focusVisible: false })}
        style={{ ...positioningContainerStyle, left: `${positionAsPercentage}%`, }}
      >
        {/* A vertical bar when the handle is 'pressed' (i.e. being dragged) */}
        {renderTouchDragIndicator &&
          <span style={pressIndicationBarStyle} data-tb-test-id={dataTestId + '-touch-drag-indicator'} />
        }
        {/* This is a wider area than the icon, making it easier to drag the icon by touch.*/}
        <span style={interactiveAreaStyle} ref={this.handleInteractiveAreaRef} />
        {/* The icon */}
        <svg
          height={SliderHandleIconSize}
          width={SliderHandleIconSize}
          {...this.props.ariaAttributes}
          aria-disabled={disabled}
          aria-orientation='horizontal'
          children={svgPaths}
          className={TypeStyle.classes(SliderHandleStyleName, SliderHandleSvgStyleName, {
            isActive,
            'left': iconType === SliderHandleIconType.Left,
            'right': iconType === SliderHandleIconType.Right,
          })}
          data-tb-test-id={makeTestId(this.props.testId, 'SliderHandleIconArea')}
          onKeyDown={!disabled ? this.props.onKeyDown : undefined}
          onKeyUp={!disabled ? this.props.onKeyUp : undefined}
          ref={this.handleImgRef}
          role='slider'
          tabIndex={disabled ? undefined : 0}
        />
      </span>
    );
  }
}
